// Import-run journal in IndexedDB (survives localStorage.clear() — the sidebar
// Logout button wipes localStorage). The journal is a hint, never truth: the
// runner always reconciles against the server before creating anything.

const DB_NAME = "ttStopsImport";
const STORE = "journal";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export interface JournalEntry {
  ts: string;
  type: "upload" | "stop_created" | "tail_done" | "verify";
  sl?: number;
  kind?: "image" | "audio";
  lang?: string;
  file?: string;
  id?: number | null;
  [k: string]: any;
}

const key = (tourId: number) => `tour${tourId}`;

export const journal = {
  async readAll(tourId: number): Promise<JournalEntry[]> {
    return (await withStore<JournalEntry[]>("readonly", (s) => s.get(key(tourId)))) || [];
  },

  async append(tourId: number, entry: Omit<JournalEntry, "ts">): Promise<void> {
    const all = await this.readAll(tourId);
    all.push({ ts: new Date().toISOString(), ...entry } as JournalEntry);
    await withStore("readwrite", (s) => s.put(all, key(tourId)));
  },

  // uploaded server-filenames per stop, and which stops were created
  async summarize(tourId: number): Promise<{ uploads: Record<number, { image: string | null; audio: Record<string, string> }>; created: Set<number> }> {
    const uploads: Record<number, { image: string | null; audio: Record<string, string> }> = {};
    const created = new Set<number>();
    for (const e of await this.readAll(tourId)) {
      if (e.type === "upload" && e.sl != null) {
        uploads[e.sl] = uploads[e.sl] || { image: null, audio: {} };
        if (e.kind === "image") uploads[e.sl].image = e.file || null;
        else if (e.lang) uploads[e.sl].audio[e.lang] = e.file || "";
      } else if (e.type === "stop_created" && e.sl != null) {
        created.add(e.sl);
      }
    }
    return { uploads, created };
  },

  // Archive after a successful run so a future fresh import into the same tour
  // can never replay stale filenames.
  async archive(tourId: number): Promise<void> {
    const all = await this.readAll(tourId);
    if (all.length) await withStore("readwrite", (s) => s.put(all, `${key(tourId)}.done-${Date.now()}`));
    await withStore("readwrite", (s) => s.delete(key(tourId)));
  },

  async hasActive(tourId: number): Promise<boolean> {
    return (await this.readAll(tourId)).length > 0;
  },
};
